import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { EllipsisVerticalIcon } from '@heroicons/react/24/outline';
import { cn } from '../../utils/cn';
import { t } from '../../utils/translations';
import { IconButton, ButtonIcon } from './Button';

export interface ActionMenuItem {
  label: string;
  icon: ButtonIcon;
  onClick: () => void;
  tone?: 'neutral' | 'danger';
}

export interface ActionMenuProps {
  items: ActionMenuItem[];
  /** Accessible name for the trigger. Defaults to the shared "Acțiuni" label. */
  label?: string;
  className?: string;
}

const MENU_WIDTH = 176;
const GAP = 4;
const VIEWPORT_MARGIN = 8;
const ITEM_HEIGHT = 36;

/**
 * The "⋮" menu that collapses a row's actions behind one trigger.
 *
 * The dropdown is portalled to `document.body` and positioned with `fixed` rather than
 * absolutely inside the trigger's parent. Cards clip their children (`overflow-hidden` for the
 * rounded accent bar, `overflow-auto` on scroll panels), which silently cut the menu in half.
 * Escaping the stacking context is the only fix that does not require every future card to
 * remember not to clip.
 */
const ActionMenu: React.FC<ActionMenuProps> = ({ items, label, className }) => {
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState<{ top: number; left: number } | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const updatePosition = useCallback(() => {
    const trigger = triggerRef.current;
    if (!trigger) return;

    const rect = trigger.getBoundingClientRect();
    const menuHeight = menuRef.current?.offsetHeight ?? items.length * ITEM_HEIGHT + 8;
    const spaceBelow = window.innerHeight - rect.bottom;

    const flipUp = spaceBelow < menuHeight + GAP + VIEWPORT_MARGIN && rect.top > menuHeight;
    const top = flipUp ? rect.top - menuHeight - GAP : rect.bottom + GAP;

    const maxLeft = window.innerWidth - MENU_WIDTH - VIEWPORT_MARGIN;
    const left = Math.max(VIEWPORT_MARGIN, Math.min(rect.right - MENU_WIDTH, maxLeft));

    setPosition({ top, left });
  }, [items.length]);

  useLayoutEffect(() => {
    if (!open) return;
    updatePosition();
  }, [open, updatePosition]);

  useEffect(() => {
    if (!open) return;

    const close = () => setOpen(false);
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') close();
    };

    // A fixed menu does not follow its trigger, so dismiss instead of drifting.
    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
    };
  }, [open]);

  if (items.length === 0) return null;

  return (
    <div className={cn('shrink-0', className)}>
      <IconButton
        ref={triggerRef}
        icon={EllipsisVerticalIcon}
        label={label ?? t.common.actions}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
      />
      {open &&
        createPortal(
          <>
            <div
              className="fixed inset-0 z-[1500]"
              aria-hidden
              onClick={() => setOpen(false)}
            />
            <div
              ref={menuRef}
              role="menu"
              style={{
                top: position?.top ?? 0,
                left: position?.left ?? 0,
                width: MENU_WIDTH,
                visibility: position ? 'visible' : 'hidden',
              }}
              className="fixed z-[1501] overflow-hidden rounded-lg border border-slate-200 bg-white py-1 shadow-lg"
            >
              {items.map((item) => {
                const Icon = item.icon;
                return (
                  <button
                    key={item.label}
                    type="button"
                    role="menuitem"
                    onClick={() => {
                      setOpen(false);
                      item.onClick();
                    }}
                    className={cn(
                      'flex w-full items-center gap-2 px-3 py-2 text-left text-sm transition active:scale-[0.98]',
                      item.tone === 'danger'
                        ? 'text-red-600 hover:bg-red-50 active:text-red-700'
                        : 'text-slate-700 hover:bg-slate-50 hover:text-brand-700 active:text-brand-800'
                    )}
                  >
                    <Icon className="h-4 w-4 shrink-0" aria-hidden="true" />
                    {item.label}
                  </button>
                );
              })}
            </div>
          </>,
          document.body
        )}
    </div>
  );
};

export default ActionMenu;
